import html2pdf from "html2pdf.js";
import { extractFieldValues } from "./Fieldmapping";
import { APP_CONFIG } from "../../config/appConfig";

interface ApplicationData {
    applicationId?: string;
    [key: string]: any;
}

/**
 * Generate a filled PDF document from application data using HTML
 * This approach properly handles Marathi/Devanagari fonts
 */
export async function generateFilledPDFDocument(rawData: ApplicationData): Promise<Blob> {
    console.log("========================================");
    console.log("📄 PDF GENERATOR - DATA EXTRACTION");
    console.log("========================================");

    // Extract application ID
    const applicationId = rawData.applicationId || rawData.application_id || rawData.id || "";
    console.log("\n📋 Application ID:", applicationId);

    // Extract and map field values
    console.log("\n🔍 Extracting Field Values...");
    const data = extractFieldValues(rawData);

    console.log("\n✅ Extracted Data:");
    console.log("  plot_owner_name:", data.plot_owner_name);
    console.log("  layout_name:", data.layout_name);
    console.log("  mauza:", data.mauza);
    console.log("  plot_number:", data.plot_number);
    console.log("  khasara_number:", data.khasara_number);
    console.log("  area_of_plot:", data.area_of_plot);
    console.log("  is_there_construction:", data.is_there_construction);
    console.log("  construction_area:", data.construction_area);
    console.log("  court_case:", data.court_case);
    console.log("  mobile_number:", data.mobile_number);
    console.log("  email_id:", data.email_id);
    console.log("\n========================================\n");

    // Create HTML content with proper styling
    const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;700&display=swap">
        
        body {
          font-family: 'Noto Sans Devanagari', sans-serif;
          padding: 30px 40px;
          line-height: 1.8;
          color: #000;
          font-size: 12px;
          padding: 30px 40px 60px 40px;
        }
        
        .header {
          text-align: center;
          margin-bottom: 25px;
        }
        
        .header h1 {
          font-size: 20px;
          font-weight: bold;
          margin: 0 0 8px 0;
          letter-spacing: 0.5px;
        }
        
        .header p {
          font-size: 14px;
          margin: 5px 0;
        }
        
        .divider {
          border: none;
          border-top: 1px solid #000;
          margin: 15px 0;
        }
        
        .app-number {
          margin: 20px 0 25px 0;
          font-size: 14px;
        }
        
        .address-section {
          margin: 10px 0 20px 0;
          line-height: 1.6;
        }
        
        .address-section p {
          margin: 3px 0;
        }
        
        .subject {
          margin: 20px 0;
          font-weight: bold;
          text-decoration: underline;
          text-align: center;
        }
        
        .salutation {
          margin: 15px 0 10px 0;
        }
        
        .intro {
          margin: 10px 0 25px 0;
          text-indent: 40px;
        }
        
        .field-row {
          margin: 12px 0;
          line-height: 1.8;
        }
        
        .field-number {
          display: inline-block;
          width: 20px;
        }
        
        .section-title {
          font-weight: bold;
          margin: 25px 0 12px 0;
        }
        
        .document-list {
          margin: 15px 0 15px 40px;
          font-size: 12px;
        }
        
        .document-item {
          margin: 7px 0;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        
        .document-item span:first-child {
          flex: 1;
          padding-right: 10px;
        }
        
        .document-item span:last-child {
          white-space: nowrap;
          min-width: 80px;
          text-align: right;
        }
        
        .document-item-indent {
          margin: 6px 0 6px 30px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        
        .document-item-indent span:first-child {
          flex: 1;
          padding-right: 10px;
        }
        
        .document-item-indent span:last-child {
          white-space: nowrap;
          min-width: 80px;
          text-align: right;
        }
        
        .payment-text {
          margin: 20px 0;
          text-align: justify;
          line-height: 1.8;
        }
        
        .declaration {
          margin: 25px 0;
          text-indent: 40px;
        }
        
        .signature-section {
          margin: 30px 0 40px 0;
        }
        
        .signature-row {
          display: flex;
          justify-content: space-between;
          margin: 8px 0;
        }
        
        .receipt-title {
          text-align: center;
          font-weight: bold;
          margin: 50px 0 20px 0;
          font-size: 15px;
        }
        
        .receipt-content {
          margin: 20px 0;
          line-height: 1.8;
        }
        
        .signature-right {
          text-align: right;
          margin-top: 60px;
          font-size: 15px;
          margin-bottom: 40px;
        }
        
        .page-break {
          page-break-before: always;
        }
      </style>
    </head>
    <body>
      <!-- Page 1: Application Form -->
      <div class="header">
        <h1>नगर पंचायत बहादुरा</h1>
        <p>ता. नागपूर जि. नागपूर</p>
      </div>
      
      <hr class="divider">
      
      <div class="app-number">
        आवेदन पत्र क्र : <strong>${applicationId || ""}</strong>
      </div>
      
      <div class="address-section">
        <p>मा. मुख्याधिकारी</p>
        <p>नगर पंचायत बहादुरा</p>
        <p>ता. नागपूर जि. नागपूर</p>
      </div>
      
      <div class="subject">
        <span> विषय :- अनधिकृत अभिन्यासातील भूखंडाच्या नियमितीकरण बाबत आवेदन पत्र. </span>
      </div>
      
      <div class="salutation">महोदय,</div>
      
      <div class="intro">
        वैयक्तिकरित्या भूखंडाचे नियमितीकरण करण्यासाठी दिल्याप्रमाणे माहिती सादर आहे.
      </div>
      
      <!-- Details Section -->
      <div class="field-row">
        <span class="field-number">१.</span> भूखंडधारकाचे पूर्ण नाव 
        : <strong>${data.plot_owner_name || ""}</strong>
      </div>
      
      <div class="field-row">
        <span class="field-number">२.</span>अनधिकृत अभिन्यासाचे नाव 
        : <strong>${data.layout_name || ""}</strong>
      </div>
      
      <div class="field-row">
        <span class="field-number">३.</span> मौजाचे नाव : <strong>${data.mauza || ""}</strong> 
        भूखंड क्रमांक : <strong>${data.plot_number || ""}</strong> 
        खसरा क्रमांक : <strong>${data.khasara_number || ""}</strong>
      </div>
      
      <div class="field-row">
        <span class="field-number">४.</span> भूखंडाचे क्षेत्रफळ ( विक्रीप्रमाणे ) 
        : <strong>${data.area_of_plot || ""}</strong> चौ.मी.
      </div>
      
      <div class="field-row">
        <span class="field-number">५.</span> बांधकाम आहे काय ? 
        : <strong>${data.is_there_construction || "नाही"}</strong>
      </div>
      
      ${(String(data.is_there_construction) === "होय" || String(data.is_there_construction) === "true" || String(data.is_there_construction) === "1") && Number(data.construction_area || 0) > 0 ? `
      <div class="field-row">
        <span class="field-number">६.</span> असल्यास बांधकामाचे क्षेत्रफळ 
        : <strong>${data.construction_area && data.construction_area !== "NaN" ? data.construction_area : "0"}</strong> चौ. मी. / चौ. फुट
      </div>
      ` : ''}
      
      <div class="field-row">
        <span class="field-number">७.</span> भूखंड किंवा बांधकामाविषयी न्यायालयात
      </div>
      <div class="field-row" style="margin-left: 20px;">
        प्रकरण प्रलंबित आहे काय ?( असल्यास तपशील द्यावा ) 
        : <strong>${data.court_case || "नाही"}</strong>
      </div>
      
      <div class="field-row">
        <span class="field-number">८.</span> मोबाईल क्रमांक 
        : <strong>${data.mobile_number || ""}</strong>
      </div>
      
      <div class="field-row">
        <span class="field-number">९.</span> ई-मेल आय.डी. 
        : <strong>${data.email_id || ""}</strong>
      </div>
      
      <!-- Documents Section -->
      <div class="section-title">सोबत जोडावयाचे दस्तावेज :</div>
      <div style="margin: 10px 0;">खालीलप्रमाणे कागदपत्रे जोडण्यात यावे.</div>
      
      <div class="document-list">
        <div class="document-item">
          <span>१. विहित अर्ज नमुना</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item">
          <span>२. चालु वर्षाची कर पावती</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item">
          <span>३. नमुना ८-अ ( कर आकारणी यादी)</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item">
          <span>४. १०० रु. च्या STAMP पेपर वर :-</span>
          <span></span>
        </div>
        <div class="document-item-indent">
          <span>- हमीपत्र :</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item-indent">
          <span>- क्षतीपूर्ती :</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item">
          <span>५. तलाठी नकाशा</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item">
          <span>६. चालू वर्षाचा ७/१२ उतारा</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item">
          <span>७. २०२० पूर्वीची विक्रीपत्र</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item">
          <span>८. ले-आउट नकाशा</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item">
          <span>९. भूखंडाचे ARCHITECT कडून L. S. MAP</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item">
          <span>१०. मोजणीची 'क' प्रत</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item">
          <span>११. इमारत्त नकाशा ४ प्रती (बांधकाम असल्यास )</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item">
          <span>१२. GEO - TAG फोटो (भूखंड स्वच्छ करून )</span>
          <span>होय / नाही</span>
        </div>
        <div class="document-item">
          <span>१३. आधार कार्ड</span>
          <span>होय / नाही</span>
        </div>
      </div>
      
      <!-- Payment Info -->
      <div class="payment-text">
        अग्रिम रक्कम जमा करण्यासाठी नगर पंचायत बहादुरा च्या नावे बँकेचे नाव -------------------- 
        बँकेची डिमांड ड्राफ्ट / पे ऑर्डर किंवा रोख रक्कम ( प्रती भूखंड ) रुपये १०००/- फक्त पावती क्रमांक 
        --------------दिनांक ------------------ ( डिमांड ड्राफ्ट / पे ऑर्डर फक्त नगर पंचायत बहादुरा 
        कार्यालयात स्वीकारले जातील) एकूण रक्कम रु. १०००/- जमा केलेल्या रकमेच्या पावतीची छायाप्रत अर्जासोबत जोडवी.
      </div>
      
      <!-- Declaration -->
      <div class="declaration">
        वरील माहिती माझ्या माहितीप्रमाणे सत्य आहे. खोटी आढळल्यास मी कायदेशीर कार्यवाहीस पात्र राहीन.
      </div>
      
      <!-- Signature Section -->
      <div class="signature-section">
        <div>स्थळ : बहादुरा</div>
        <div class="signature-row">
          <span>दिनांक :&nbsp;&nbsp;&nbsp;&nbsp;/&nbsp;&nbsp;&nbsp;&nbsp;/</span>
          <span style="margin-right: 80px;">भूखंडधारकाची स्वाक्षरी</span>
        </div>
      </div>
      
      <!-- Page 2: Receipt -->
        <div class="receipt-title">पोच पवती</div>
        <hr class="divider">
        
        <div class="receipt-content">
          <p style="margin: 15px 0;">
            आवेदन पत्र क्रमांक <strong>${applicationId || ""}</strong>
          </p>
          
          <p style="margin: 15px 0;">
            श्री/श्रीमती <strong>${data.plot_owner_name || ""}</strong> 
            यांचे ( अभिन्यासाचे नाव ) <strong>${data.layout_name || ""}</strong> 
            खसरा क्रमांक <strong>${data.khasara_number || ""}</strong> 
            मौजा <strong>${data.mauza || ""}</strong> 
            यातील भूखंड क्रमांक <strong>${data.plot_number || ""}</strong> 
            चे नियमीतीकरणाबाबत आवेदन पत्र प्राप्त झाले
          </p>
        </div>
        
        <div class="signature-right">
          प्राप्तकर्त्याची स्वाक्षरी
        </div>
    </body>
    </html>
  `;

    // Create a temporary element
    const element = document.createElement("div");
    element.innerHTML = htmlContent;

    // Configure html2pdf options
    const options = {
        margin: [10, 10, 10, 10],
        filename: `Application_Form_${applicationId || "Draft"}.pdf`,
        image: { type: "png", quality: 1 },
        html2canvas: {
            scale: 2,
            useCORS: true,
            letterRendering: true,
        },
        jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait"
        },
    };

    // Generate PDF and return as blob
    return new Promise((resolve, reject) => {
        html2pdf()
            .set(options)
            .from(element)
            .outputPdf("blob")
            .then((blob: Blob) => {
                resolve(blob);
            })
            .catch((error: Error) => {
                reject(error);
            });
    });
}
